import { onConsentReady, hasVendorGrant } from './gdpr_consent'
import { getEmbedParent, getEnvironment, inBrowser } from 'shared/javascripts/util/environment'
import { deleteCookies } from 'shared/javascripts/util/cookies'
import { parsedFrontendConfig } from './frontend_config'

// This module mirrors amplitude.ts: the raw fbq snippet has no consent-awareness of its own (unlike
// GTM, which integrates with Google Consent Mode), so we initialize it lazily on the first push, which
// is only reached after the consent gate in metaPixelPush() below.

declare global {
  interface Window {
    fbq: {
      (...args: unknown[]): void
      callMethod?: (...args: unknown[]) => void
      queue: unknown[]
      push: Window['fbq']
      loaded: boolean
      version: string
    }
    _fbq: Window['fbq']
    metaPixelEvents: string[]
  }
}

const pixelId = parsedFrontendConfig('metaPixelId') as string | null
let metaPixelInitialized = false

if (!pixelId && inBrowser()) {
  window.metaPixelEvents = []
}

function ensureMetaPixelInitialized(): void {
  if (metaPixelInitialized) {
    return
  }

  ;(function (f: any, b: Document, e: string, v: string) {
    if (f.fbq) return
    let n: any = (f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
    })
    if (!f._fbq) f._fbq = n
    n.push = n
    n.loaded = true
    n.version = '2.0'
    n.queue = []
    const t: any = b.createElement(e)
    t.async = true
    t.src = v
    const s = b.getElementsByTagName(e)[0]
    s.parentNode!.insertBefore(t, s)
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js')

  window.fbq('init', pixelId)
  metaPixelInitialized = true
}

interface MetaEvent {
  name: string
  // Picks the fbq method: true → 'track' (Meta's predefined "Standard Events" like PageView,
  // CompleteRegistration), false → 'trackCustom' (our own event names).
  standardEvent: boolean
}

const META_EVENT_MAP: Record<string, (eventData: Record<string, any>) => MetaEvent | null> = {
  'page.view': () => ({ name: 'PageView', standardEvent: true }),
  'registration.stepCompleted': (eventData) =>
    eventData.stepName === 'registration'
      ? { name: 'CompleteRegistration', standardEvent: true }
      : null,
  'player.googleImaEvent': (eventData) =>
    eventData.googleImaEventName === 'click' ? { name: 'AdClicked', standardEvent: false } : null,
}

function performMetaPixelPush(event: MetaEvent): void {
  if (getEnvironment() === 'development' || getEnvironment() === 'staging') {
    console.log('[Meta Pixel] Tracking event', event)
  }

  if (pixelId) {
    ensureMetaPixelInitialized()
    window.fbq(event.standardEvent ? 'track' : 'trackCustom', event.name)
  } else {
    window.metaPixelEvents.push(event.name)
  }
}

export function metaPixelPush(eventName: string, eventData: Record<string, unknown>): void {
  if (getEmbedParent()) {
    return
  }

  const metaEvent = META_EVENT_MAP[eventName]?.(eventData)
  if (!metaEvent) {
    return
  }

  onConsentReady(() => {
    if (hasVendorGrant('Meta Pixel')) {
      performMetaPixelPush(metaEvent)
    }
  })
}

export function updateMetaPixelConsent(): void {
  if (!pixelId || getEmbedParent()) {
    return
  }
  if (!hasVendorGrant('Meta Pixel')) {
    deleteCookies((name) => name === '_fbp' || name === '_fbc')
  }
}
